/**
 * Conectar o WhatsApp pelo Cadastro Incorporado da Meta.
 *
 * O botão abre o diálogo da própria Meta (SDK JS, popup). Duas coisas voltam
 * por caminhos diferentes, em qualquer ordem: o CÓDIGO pelo callback do
 * `FB.login`, e os IDs (WABA, número, portfólio) por um `postMessage` da janela
 * do diálogo. Só quando os dois chegaram chamamos o servidor — e rápido,
 * porque o código vale 30 segundos.
 *
 * Nada de token passa por aqui: o navegador entrega um código de uso único e
 * o servidor faz a troca. Os IDs que chegam pelo `postMessage` são conferidos
 * no servidor contra o que a Meta diz do token.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertCircle, CheckCircle2, Facebook, Loader2, RefreshCw, Smartphone, Trash2, Unplug } from "lucide-react";
import { toast } from "sonner";

// ─── SDK da Meta ──────────────────────────────────────────────────────────────

type FbLoginResponse = {
  authResponse?: { code?: string; accessToken?: string; expiresIn?: number; userID?: string } | null;
  status?: string;
};
type FbSdk = {
  init: (opts: { appId: string; autoLogAppEvents: boolean; xfbml: boolean; version: string }) => void;
  login: (cb: (r: FbLoginResponse) => void, opts: Record<string, unknown>) => void;
};

declare global {
  interface Window {
    FB?: FbSdk;
    fbAsyncInit?: () => void;
  }
}

let sdkPromise: Promise<FbSdk> | null = null;

/** Carrega o SDK uma vez por página e resolve quando `FB.init` rodou. */
function loadFacebookSdk(appId: string, version: string): Promise<FbSdk> {
  if (window.FB) return Promise.resolve(window.FB);
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise<FbSdk>((resolve, reject) => {
    if (!document.getElementById("fb-root")) {
      const root = document.createElement("div");
      root.id = "fb-root";
      document.body.appendChild(root);
    }
    window.fbAsyncInit = () => {
      if (!window.FB) return reject(new Error("SDK do Facebook não inicializou"));
      window.FB.init({ appId, autoLogAppEvents: true, xfbml: true, version });
      resolve(window.FB);
    };
    const s = document.createElement("script");
    s.src = "https://connect.facebook.net/pt_BR/sdk.js";
    s.async = true;
    s.defer = true;
    s.crossOrigin = "anonymous";
    s.onerror = () => {
      sdkPromise = null;
      reject(new Error("Não foi possível carregar o SDK do Facebook. Verifique bloqueadores de anúncio."));
    };
    document.body.appendChild(s);
  });
  return sdkPromise;
}

type SessaoEs = {
  event: "FINISH" | "FINISH_ONLY_WABA" | "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING";
  wabaId: string;
  phoneNumberId: string | null;
  businessId: string | null;
};

// ─── Rótulos ──────────────────────────────────────────────────────────────────

const QUALIDADE: Record<string, { label: string; className: string }> = {
  GREEN: { label: "Qualidade boa", className: "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300" },
  YELLOW: { label: "Qualidade média", className: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300" },
  RED: { label: "Qualidade baixa", className: "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300" },
};

const LIMITE: Record<string, string> = {
  TIER_50: "50 conversas/dia",
  TIER_250: "250 conversas/dia",
  TIER_1K: "1.000 conversas/dia",
  TIER_10K: "10 mil conversas/dia",
  TIER_100K: "100 mil conversas/dia",
  TIER_UNLIMITED: "Conversas ilimitadas",
};

const TIPO: Record<string, string> = {
  manual: "Credenciais informadas manualmente",
  embedded_signup: "Conectado com o Facebook",
  coexistence: "Coexistência com o app WhatsApp Business",
};

// ─── Componente ───────────────────────────────────────────────────────────────

export default function WhatsAppConnectPanel({
  compact = false,
  onConnected,
}: {
  /** Onboarding: menos texto, sem cartão de status detalhado. */
  compact?: boolean;
  onConnected?: () => void;
}) {
  const utils = trpc.useUtils();
  const { can } = usePermissions();
  const podeEditar = can("settings:edit_whatsapp");

  const config = trpc.whatsappConnect.config.useQuery();
  const status = trpc.whatsappConnect.status.useQuery();

  const [abrindo, setAbrindo] = useState<null | "novo" | "coex">(null);
  const [concluindo, setConcluindo] = useState(false);
  // A autorização do SDK chega SEGUNDOS depois de abrir (quando a pessoa já
  // autorizou o app antes), e a conta/número só chegam no fim do diálogo,
  // minutos depois. Enquanto isso a tela precisa dizer que está esperando
  // — e não apagar a autorização por um cronômetro curto.
  const [aguardandoDialogo, setAguardandoDialogo] = useState(false);
  const [confirmarDesconexao, setConfirmarDesconexao] = useState(false);

  const invalidar = useCallback(() => {
    void utils.whatsappConnect.status.invalidate();
    void utils.tenant.current.invalidate();
  }, [utils]);

  const complete = trpc.whatsappConnect.complete.useMutation({
    onSuccess: (r) => {
      invalidar();
      toast.success(
        r.authType === "coexistence"
          ? `WhatsApp ${r.displayPhone ?? ""} conectado em Coexistência. Contatos e histórico chegam nos próximos minutos.`
          : `WhatsApp ${r.displayPhone ?? ""} conectado.`,
      );
      onConnected?.();
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Não foi possível concluir a conexão")),
    onSettled: () => setConcluindo(false),
  });

  const refresh = trpc.whatsappConnect.refresh.useMutation({
    onSuccess: () => {
      invalidar();
      toast.success("Informações atualizadas");
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Não foi possível atualizar")),
  });

  const resync = trpc.whatsappConnect.resyncCoexistence.useMutation({
    onSuccess: () => {
      invalidar();
      toast.success("Sincronização pedida. Contatos e histórico chegam aos poucos.");
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Não foi possível sincronizar")),
  });

  const disconnect = trpc.whatsappConnect.disconnect.useMutation({
    onSuccess: (r) => {
      invalidar();
      setConfirmarDesconexao(false);
      toast.success(
        r.coexistence
          ? "Desconectado aqui. Para desligar também no celular: WhatsApp Business → Configurações → Conta → Plataforma de negócios."
          : "WhatsApp desconectado.",
      );
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Não foi possível desconectar")),
  });

  // Os dois pedaços da resposta do diálogo. Refs porque chegam por callbacks
  // fora do ciclo de render, em qualquer ordem.
  // `codigoRef` guarda o código de troca OU, quando o SDK não devolve código,
  // o token direto — o servidor aceita os dois e valida no debug_token.
  const codigoRef = useRef<{ code?: string; accessToken?: string } | null>(null);
  const sessaoRef = useRef<SessaoEs | null>(null);
  const aguardandoRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A pessoa cancelou no diálogo: o callback do SDK que vem em seguida, sem
  // código, não é erro e não deve mostrar um segundo aviso.
  const canceladoRef = useRef(false);
  // Tentativa descartada localmente (Cancelar, prazo, erro) enquanto a janela
  // da Meta ainda podia estar aberta. Um FINISH que chegue depois não é
  // "ponte quebrada": é só pedir para conectar de novo.
  const descartadoRef = useRef(false);

  // Telemetria da ponte com o diálogo: sem isto, quando o popup fecha e nada
  // chega, o servidor não tem uma linha de log para investigar.
  // `mutate` é estável entre renders; o objeto da mutation não é — depender
  // dele recriaria `registrar`, `tentarConcluir` e o listener a cada render.
  const registrarNoServidor = trpc.whatsappConnect.diagnostico.useMutation().mutate;
  const registrar = useCallback(
    (etapa: string, detalhe?: Record<string, string | number | boolean | null>) => {
      // eslint-disable-next-line no-console
      console.info("[WhatsAppConnect]", etapa, detalhe ?? "");
      registrarNoServidor({ etapa, detalhe });
    },
    [registrarNoServidor],
  );

  const limparEspera = useCallback(() => {
    if (aguardandoRef.current) {
      clearTimeout(aguardandoRef.current);
      aguardandoRef.current = null;
    }
  }, []);

  // Timer pendente não pode sobreviver ao componente: dispararia toast e
  // setState numa tela que já não existe.
  useEffect(() => limparEspera, [limparEspera]);

  /**
   * Volta ao estado inicial, com aviso. Usado pelos prazos, pelo Cancelar e
   * por erro vindo do diálogo.
   *
   * Marca a tentativa como descartada: a janela da Meta pode continuar aberta
   * e mandar FINISH depois — e esse FINISH tardio não pode virar "a Meta não
   * devolveu a autorização", porque quem descartou fomos nós. Ele vira um
   * pedido claro para conectar de novo.
   */
  const desistir = useCallback(
    (motivo: "sem_sessao" | "sem_credencial" | "cancelado_pelo_usuario" | "erro_da_meta") => {
      const temCodigo = !!codigoRef.current;
      const temSessao = !!sessaoRef.current;
      registrar("ponte_incompleta", { motivo, temCodigo, temSessao });
      limparEspera();
      descartadoRef.current = true;
      codigoRef.current = null;
      sessaoRef.current = null;
      setAguardandoDialogo(false);
      setAbrindo(null);
      if (motivo === "sem_sessao") {
        toast.error(
          "A janela da Meta não informou qual conta e número foram escolhidos. Se você fechou a janela antes do fim, clique em Conectar e vá até \"Concluir\".",
          { duration: 12000 },
        );
      } else if (motivo === "sem_credencial") {
        toast.error(
          "A Meta informou a conta escolhida, mas não devolveu a autorização. Tente de novo; se persistir, fale com o suporte.",
          { duration: 12000 },
        );
      }
    },
    [registrar, limparEspera],
  );

  /**
   * Um dos dois pedaços chegou; o outro ainda não.
   *
   * - Só a AUTORIZAÇÃO (caso comum): a pessoa ainda está no diálogo, que
   *   pode levar minutos (SMS, criar conta, ler QR). Esperar com estado
   *   visível e um prazo longo, só para não ficar preso para sempre se a
   *   janela foi fechada sem CANCEL.
   * - Só a SESSÃO: o diálogo terminou e a autorização não veio — isso é
   *   ponte quebrada mesmo; poucos segundos bastam.
   */
  const armarEspera = useCallback(() => {
    limparEspera();
    const credencial = codigoRef.current;
    if (credencial) {
      setAguardandoDialogo(true);
      // Token: vale horas, dá para esperar a pessoa terminar o diálogo.
      // CÓDIGO de troca: a Meta o invalida em 30 s — esperar mais é ter
      // certeza do erro "code has expired" no servidor. Se a sessão não
      // chegar nesse prazo, é melhor pedir para tentar de novo.
      const prazo = credencial.code ? 25_000 : 15 * 60_000;
      aguardandoRef.current = setTimeout(() => {
        aguardandoRef.current = null;
        if (codigoRef.current && !sessaoRef.current) desistir("sem_sessao");
      }, prazo);
    } else {
      aguardandoRef.current = setTimeout(() => {
        aguardandoRef.current = null;
        if (sessaoRef.current && !codigoRef.current) desistir("sem_credencial");
      }, 8000);
    }
  }, [limparEspera, desistir]);

  // `complete.mutate` é estável entre renders; o objeto `complete` não é. Se
  // a dependência fosse o objeto, o listener de `message` abaixo seria
  // removido e recriado a cada render do painel.
  const concluir = complete.mutate;
  const tentarConcluir = useCallback(() => {
    const credencial = codigoRef.current;
    const sessao = sessaoRef.current;
    if (!credencial || !sessao) {
      armarEspera();
      return;
    }
    limparEspera();
    codigoRef.current = null;
    sessaoRef.current = null;
    setAguardandoDialogo(false);
    setConcluindo(true);
    registrar("enviando_ao_servidor", {
      event: sessao.event,
      wabaId: sessao.wabaId,
      phoneNumberId: sessao.phoneNumberId,
      via: credencial.code ? "codigo" : "token",
    });
    concluir({
      code: credencial.code ?? null,
      accessToken: credencial.accessToken ?? null,
      event: sessao.event,
      wabaId: sessao.wabaId,
      phoneNumberId: sessao.phoneNumberId,
      businessId: sessao.businessId,
    });
  }, [concluir, armarEspera, registrar, limparEspera]);

  // Sessão do diálogo: a janela da Meta manda por postMessage.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!/\.facebook\.com$/.test(event.origin)) return;
      let data: any = event.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch {
          // O SDK troca outras mensagens com o Facebook (xd_arbiter); só
          // as do diálogo são JSON. Ruído aqui é normal.
          return;
        }
      }
      if (!data || data.type !== "WA_EMBEDDED_SIGNUP") return;

      const ev = String(data.event ?? "");
      if (descartadoRef.current && ev !== "CANCEL") {
        // A janela terminou depois que desistimos desta tentativa. O número
        // pode ter sido criado na Meta; a próxima conexão o lista pronto.
        registrar("mensagem_apos_descarte", { event: ev });
        descartadoRef.current = false;
        if (ev.startsWith("FINISH")) {
          toast.info("A janela da Meta terminou depois do cancelamento. Clique em Conectar de novo: a conta e o número já aparecem prontos para escolher.", { duration: 12000 });
        }
        return;
      }
      registrar("mensagem_do_dialogo", {
        event: ev,
        version: data.version ?? null,
        wabaId: data.data?.waba_id ?? null,
        phoneNumberId: data.data?.phone_number_id ?? null,
        currentStep: data.data?.current_step ?? null,
        errorMessage: data.data?.error_message ?? null,
      });
      if (ev === "FINISH" || ev === "FINISH_ONLY_WABA" || ev === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING") {
        sessaoRef.current = {
          event: ev,
          wabaId: String(data.data?.waba_id ?? ""),
          phoneNumberId: data.data?.phone_number_id ? String(data.data.phone_number_id) : null,
          businessId: data.data?.business_id ? String(data.data.business_id) : null,
        };
        tentarConcluir();
        return;
      }
      if (ev === "CANCEL") {
        canceladoRef.current = true;
        limparEspera();
        setAbrindo(null);
        setAguardandoDialogo(false);
        codigoRef.current = null;
        sessaoRef.current = null;
        const passo = data.data?.current_step ? ` (parou em: ${data.data.current_step})` : "";
        toast.info(`Conexão cancelada${passo}.`);
        return;
      }
      if (data.data?.error_message) {
        // Erro dentro do diálogo depois da autorização: limpar tudo, senão o
        // painel fica travado em "aguardando" ao lado do erro.
        desistir("erro_da_meta");
        toast.error(`A Meta devolveu um erro: ${data.data.error_message}`);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [tentarConcluir, registrar, limparEspera, desistir]);

  const abrir = async (modo: "novo" | "coex") => {
    if (!config.data?.disponivel) return;
    setAbrindo(modo);
    limparEspera();
    setAguardandoDialogo(false);
    canceladoRef.current = false;
    descartadoRef.current = false;
    codigoRef.current = null;
    sessaoRef.current = null;
    try {
      const FB = await loadFacebookSdk(config.data.appId, config.data.apiVersion);
      registrar("dialogo_aberto", { modo });
      FB.login(
        (response) => {
          const code = response.authResponse?.code;
          const accessToken = response.authResponse?.accessToken;
          registrar("callback_do_sdk", {
            status: response.status ?? null,
            temCodigo: !!code,
            temToken: !!accessToken,
            chaves: Object.keys(response.authResponse ?? {}).join(","),
          });
          if (!code && !accessToken) {
            setAbrindo(null);
            // Sem código e sem CANCEL: o diálogo fechou sem autorizar (ou o
            // SDK não recebeu a resposta). Avisar em vez de silenciar.
            if (!sessaoRef.current && !canceladoRef.current) {
              toast.error(
                "O diálogo fechou sem concluir a autorização. Tente de novo; se persistir, desative extensões do navegador ou use outro navegador.",
                { duration: 12000 },
              );
            }
            return;
          }
          codigoRef.current = code ? { code } : { accessToken };
          tentarConcluir();
          // Se a sessão ainda não chegou, o postMessage vem em seguida.
          setAbrindo(null);
        },
        {
          config_id: config.data.configId,
          response_type: "code",
          override_default_response_type: true,
          extras: {
            setup: {},
            // Coexistência: o diálogo pede para ler o QR no app do celular.
            featureType: modo === "coex" ? "whatsapp_business_app_onboarding" : "",
            sessionInfoVersion: "3",
          },
        },
      );
    } catch (err) {
      setAbrindo(null);
      toast.error(err instanceof Error ? err.message : "Não foi possível abrir o diálogo da Meta");
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const st = status.data;
  const disponivel = !!config.data?.disponivel;
  const ocupado = abrindo !== null || concluindo || aguardandoDialogo;
  const conectado = !!st?.connected;
  const vencimento = st?.tokenExpiresAt ? new Date(st.tokenExpiresAt) : null;
  const diasParaVencer = vencimento ? Math.ceil((vencimento.getTime() - Date.now()) / 86_400_000) : null;
  const vencido = diasParaVencer !== null && diasParaVencer <= 0;
  const venceLogo = diasParaVencer !== null && diasParaVencer > 0 && diasParaVencer <= 7;
  const problema = (st?.status && st.status !== "connected") || vencido;

  return (
    <div className="space-y-4">
      {/* Estado atual */}
      {conectado && (
        <Card className={problema ? "border-red-200 bg-red-50/40 dark:bg-red-950/10" : "border-green-200 bg-green-50/40 dark:bg-green-950/10"}>
          <CardContent className="pt-5 space-y-3">
            <div className="flex items-start gap-3">
              {problema ? (
                <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">
                  {st.displayPhone ?? st.phoneNumberId}
                  {st.verifiedName ? <span className="text-muted-foreground font-normal"> · {st.verifiedName}</span> : null}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{TIPO[st.authType] ?? st.authType}</p>
                {problema && (
                  <p className="text-xs text-red-700 dark:text-red-300 mt-2">
                    {vencido
                      ? "A autorização da Meta venceu. Clique em Reconectar para renovar."
                      : (st.statusReason ?? "A conexão com a Meta caiu. Reconecte o número.")}
                  </p>
                )}
                {!problema && venceLogo && (
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                    A autorização da Meta vence em {diasParaVencer} {diasParaVencer === 1 ? "dia" : "dias"}. Clique em
                    Reconectar para renovar sem interromper o atendimento.
                  </p>
                )}
                {!compact && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {st.qualityRating && QUALIDADE[st.qualityRating] && (
                      <Badge variant="secondary" className={`text-[10px] ${QUALIDADE[st.qualityRating].className}`}>
                        {QUALIDADE[st.qualityRating].label}
                      </Badge>
                    )}
                    {st.messagingLimit && (
                      <Badge variant="secondary" className="text-[10px]">
                        {LIMITE[st.messagingLimit] ?? st.messagingLimit}
                      </Badge>
                    )}
                    {st.authType === "coexistence" && (
                      <Badge variant="secondary" className="text-[10px]">
                        <Smartphone className="h-3 w-3 mr-1" />
                        {st.coexSyncRequestedAt ? "Contatos e histórico sincronizados" : "Sincronização pendente"}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>

            {!compact && podeEditar && st.authType !== "manual" && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => refresh.mutate()} disabled={refresh.isPending}>
                  {refresh.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                  Atualizar
                </Button>
                {st.authType === "coexistence" && (
                  <Button size="sm" variant="outline" onClick={() => resync.mutate()} disabled={resync.isPending}>
                    {resync.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Smartphone className="h-3.5 w-3.5 mr-1.5" />}
                    Sincronizar contatos e histórico
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setConfirmarDesconexao(true)}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  Desconectar
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Conectar */}
      {disponivel && podeEditar && (
        <div className="space-y-3">
          <Button
            className="w-full bg-[#1877F2] text-white hover:bg-[#166FE5]"
            onClick={() => abrir("novo")}
            disabled={ocupado}
          >
            {abrindo === "novo" || concluindo ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Facebook className="mr-2 h-4 w-4" />}
            {conectado ? "Reconectar ou trocar o número" : "Conectar WhatsApp com o Facebook"}
          </Button>

          {config.data?.coexistencia && (
            <Button variant="outline" className="w-full" onClick={() => abrir("coex")} disabled={ocupado}>
              {abrindo === "coex" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Smartphone className="mr-2 h-4 w-4" />}
              Conectar o número que já uso no app WhatsApp Business
            </Button>
          )}

          {aguardandoDialogo && !concluindo && (
            <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50/60 px-3 py-2 text-xs text-blue-900 dark:border-blue-900/40 dark:bg-blue-950/20 dark:text-blue-200">
              <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
              <span className="flex-1">
                Autorização recebida. Termine na janela da Meta e clique em <strong>Concluir</strong> — a conexão
                fecha aqui sozinha.
              </span>
              <button
                type="button"
                className="underline underline-offset-2 hover:opacity-80"
                onClick={() => desistir("cancelado_pelo_usuario")}
              >
                Cancelar
              </button>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            {concluindo
              ? "Concluindo a conexão com a Meta…"
              : config.data?.coexistencia
                ? "Na primeira opção você cria ou escolhe um número na Meta. Na segunda, lê um QR no celular e continua usando o app normalmente — as conversas aparecem aqui também. Nenhuma senha passa pelo LeadConversa."
                : "Você escolhe a conta e o número na tela da Meta e volta para cá. Nenhuma senha passa pelo LeadConversa."}
          </p>
        </div>
      )}

      {!disponivel && !config.isLoading && (
        <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/10">
          <CardContent className="pt-4 flex items-start gap-3">
            <Unplug className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 dark:text-amber-300">
              {compact
                ? "A conexão com o Facebook ainda não está habilitada neste ambiente. Você pode seguir e conectar depois em Configurações → Integrações → WhatsApp (modo avançado)."
                : "A conexão com o Facebook ainda não está habilitada neste ambiente. Use as credenciais manuais abaixo ou fale com o suporte."}
            </p>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={confirmarDesconexao} onOpenChange={setConfirmarDesconexao}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desconectar o WhatsApp?</AlertDialogTitle>
            <AlertDialogDescription>
              O envio e o recebimento de mensagens param na hora. Campanhas agendadas e fluxos deixam de disparar até
              você conectar de novo.
              {st?.authType === "coexistence" &&
                " Na Coexistência, o app do celular continua funcionando; para desligar a integração também por lá, use Configurações → Conta → Plataforma de negócios no WhatsApp Business."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                disconnect.mutate();
              }}
            >
              {disconnect.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Desconectar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
